import React, { ReactElement, Fragment } from 'react';
import Card from '../../../../components/card/card.component';
import Widget from '../../../../components/widget/widget.component';
import WidgetRow from '../../../../components/widget-row/widget-row.component';
import Table from '../../../../components/table/table.component';
import TableHead from '../../../../components/table-head/table-head.component';
import TableBody from '../../../../components/table-body/table-body.component';
import TableRow from '../../../../components/table-row/table-row.component';
import TableCell from '../../../../components/table-cell/table-cell.component';
import TextBlock from '../../../../components/text-block/text-block.component';
import ActionButton from '../../../../components/action-button/action-button.component';
import FlexContainer from '../../../../components/flex-container/flex-container.component';
import ExpansionGroup from '../../../../components/expansion-group/expansion-group.component';
import NoContentBlock from '../../../../components/no-content-block/no-content-block.component';
import TableRowAddNewItem from '../../../../components/table-row-add-new-item/table-row-add-new-item.component';
import VisualEditorSectionBlock
  from '../../../../components/visual-editor-section-block/visual-editor-section-block.component';
import FormFieldText from '../../../../components/form-field-text/form-field-text.component';
import SinglePageTab from '../../../app/common/single-page-tab/single-page-tab.component';
import RemoveIcon from '../../../../icons/remove-icon';
import FormService from '../../../../services/form-service';
import useScreenSmall from '../../../../hooks/use-screen-small';

const DealTemplateCapitalization: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const COLUMNS_TOTAL_EDIT: number = 2;
  const COLUMNS_TOTAL_DEFAULT: number = 1;

  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isActive,
    isRightDrawerOpen,
    formError,
    dealTemplateInitialData,
    onCancelForm,
    onSubmitForm,
    onDealCapitalizationSourceGroupAdd,
    onDealCapitalizationSourceGroupDelete,
    onDealCapitalizationUseGroupAdd,
    onDealCapitalizationUseGroupDelete,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();

  const initialDataFormPrefix: string = 'initialData';
  const dealTemplateInitialDataDealCapitalizationSourcesGroups: DealCapitalizationGroup[] = (dealTemplateInitialData
    && dealTemplateInitialData.dealCapitalization && dealTemplateInitialData.dealCapitalization.sources) || [];
  const dealTemplateInitialDataDealCapitalizationUsesGroups: DealCapitalizationGroup[] = (dealTemplateInitialData
    && dealTemplateInitialData.dealCapitalization && dealTemplateInitialData.dealCapitalization.uses) || [];

  return (
    <SinglePageTab
      isSaving={isSaving}
      isSaveDisabled={isSaveDisabled}
      isEditMode={isEditMode}
      isHidden={!isActive}
      isRightDrawerOpen={isRightDrawerOpen}
      formError={formError}
      onCancelForm={onCancelForm}
      onSubmitForm={onSubmitForm}
    >
      <div>
        <ExpansionGroup
          isFixed
          isExpanded
          isDefaultExpanded
          withExpandedOffset
          headerComponent="Capitalization Common Info"
        >
          <Card isContainer>
            <Table>
              <TableBody
                withNoBorders
                withTopAlign
                withSmallPadding
                withTwoEqualColumns={!isSmallScreen}
                withBlockCells={isSmallScreen}
              >
                <TableRow>
                  <TableCell>
                    <FlexContainer isSpread>
                      <TextBlock>
                        Total Capitalization
                      </TextBlock>
                      <VisualEditorSectionBlock isDisabled withNoEdit isInlineField isEditMode={isEditMode} />
                    </FlexContainer>
                  </TableCell>
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell>
                    <FlexContainer isSpread>
                      <TextBlock>
                        ZFA
                      </TextBlock>
                      <VisualEditorSectionBlock isDisabled withNoEdit isInlineField isEditMode={isEditMode} />
                    </FlexContainer>
                  </TableCell>
                  <TableCell>
                    <FlexContainer isSpread>
                      <TextBlock>
                        GSF
                      </TextBlock>
                      <VisualEditorSectionBlock isDisabled withNoEdit isInlineField isEditMode={isEditMode} />
                    </FlexContainer>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <FlexContainer isSpread>
                      <TextBlock>
                        NSF
                      </TextBlock>
                      <VisualEditorSectionBlock isDisabled withNoEdit isInlineField isEditMode={isEditMode} />
                    </FlexContainer>
                  </TableCell>
                  <TableCell>
                    <FlexContainer isSpread>
                      <TextBlock>
                        Units
                      </TextBlock>
                      <VisualEditorSectionBlock isDisabled withNoEdit isInlineField isEditMode={isEditMode} />
                    </FlexContainer>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <FlexContainer isSpread>
                      <TextBlock>
                        Keys
                      </TextBlock>
                      <VisualEditorSectionBlock isDisabled withNoEdit isInlineField isEditMode={isEditMode} />
                    </FlexContainer>
                  </TableCell>
                  <TableCell>
                    <FlexContainer isSpread>
                      <TextBlock>
                        Acres
                      </TextBlock>
                      <VisualEditorSectionBlock isDisabled withNoEdit isInlineField isEditMode={isEditMode} />
                    </FlexContainer>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <FlexContainer isSpread>
                      <TextBlock>
                        Lot SF
                      </TextBlock>
                      <VisualEditorSectionBlock isDisabled withNoEdit isInlineField isEditMode={isEditMode} />
                    </FlexContainer>
                  </TableCell>
                  <TableCell />
                </TableRow>
                <TableRow>
                  <TableCell>
                    <FlexContainer isSpread>
                      <TextBlock>
                        Deal Term
                      </TextBlock>
                      <VisualEditorSectionBlock isDisabled withNoEdit isInlineField isEditMode={isEditMode} />
                    </FlexContainer>
                  </TableCell>
                  <TableCell>
                    <FlexContainer isSpread>
                      <TextBlock>
                        Projected NOI
                      </TextBlock>
                      <VisualEditorSectionBlock isDisabled withNoEdit isInlineField isEditMode={isEditMode} />
                    </FlexContainer>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Card>
        </ExpansionGroup>
        <ExpansionGroup isFixed isExpanded isDefaultExpanded headerComponent="Sources">
          {
            ((dealTemplateInitialDataDealCapitalizationSourcesGroups
              && !!dealTemplateInitialDataDealCapitalizationSourcesGroups.length) || isEditMode) ? (
              <Table isFullWidth>
                <TableHead isSubHeader withFixedCellsSize>
                  {
                    dealTemplateInitialDataDealCapitalizationSourcesGroups.map((
                      dealCapitalizationGroup: DealCapitalizationGroup,
                    ): ReactElement => {
                      const formKeyDealCapitalizationGroup: string = FormService.getIndexedFormFieldKey(
                        dealCapitalizationGroup.id,
                      );

                      return (
                        <TableRow key={dealCapitalizationGroup.id}>
                          <TableCell>
                            {
                              (!isEditMode || dealCapitalizationGroup.isSystem) && (
                                dealCapitalizationGroup.name
                              )
                            }
                            {
                              (isEditMode && !dealCapitalizationGroup.isSystem) && (
                                <Fragment>
                                  <FormFieldText
                                    isHidden
                                    name={`${initialDataFormPrefix}.dealCapitalization.sources`
                                      + `[${formKeyDealCapitalizationGroup}].id`}
                                  />
                                  <FormFieldText
                                    isLight
                                    isLimited
                                    withUnderline
                                    placeholder="Name"
                                    name={`${initialDataFormPrefix}.dealCapitalization.sources`
                                      + `[${formKeyDealCapitalizationGroup}].name`}
                                  />
                                </Fragment>
                              )
                            }
                          </TableCell>
                          <TableCell isTextRight>
                            {
                              isEditMode && !dealCapitalizationGroup.isSystem && (
                                <ActionButton
                                  isLight
                                  isInline
                                  withNoHighlight
                                  withNoPadding
                                  onClick={(): void => {
                                    onDealCapitalizationSourceGroupDelete(dealCapitalizationGroup.id);
                                  }}
                                >
                                  <RemoveIcon />
                                </ActionButton>
                              )
                            }
                          </TableCell>
                        </TableRow>
                      );
                    })
                  }
                  {
                    isEditMode && (
                      <TableRowAddNewItem
                        isLight
                        label="Add New Group"
                        colSpan={COLUMNS_TOTAL_EDIT}
                        onClick={onDealCapitalizationSourceGroupAdd}
                      />
                    )
                  }
                </TableHead>
              </Table>
            ) : (
              <NoContentBlock isExtended>
                No deal capitalization sources groups
              </NoContentBlock>
            )
          }
        </ExpansionGroup>
        <ExpansionGroup isFixed isExpanded isDefaultExpanded withOffset headerComponent="Uses">
          {
            ((dealTemplateInitialDataDealCapitalizationUsesGroups
              && !!dealTemplateInitialDataDealCapitalizationUsesGroups.length) || isEditMode) ? (
              <Table isFullWidth>
                <TableHead isSubHeader withFixedCellsSize>
                  {
                    dealTemplateInitialDataDealCapitalizationUsesGroups.map((
                      dealCapitalizationGroup: DealCapitalizationGroup,
                    ): ReactElement => {
                      const formKeyDealCapitalizationGroup: string = FormService.getIndexedFormFieldKey(
                        dealCapitalizationGroup.id,
                      );

                      return (
                        <TableRow key={dealCapitalizationGroup.id}>
                          <TableCell>
                            {
                              (!isEditMode || dealCapitalizationGroup.isSystem) && dealCapitalizationGroup.name
                            }
                            {
                              (isEditMode && !dealCapitalizationGroup.isSystem) && (
                                <Fragment>
                                  <FormFieldText
                                    isHidden
                                    name={`${initialDataFormPrefix}.dealCapitalization.uses`
                                      + `[${formKeyDealCapitalizationGroup}].id`}
                                  />
                                  <FormFieldText
                                    isLight
                                    isLimited
                                    withUnderline
                                    placeholder="Name"
                                    name={`${initialDataFormPrefix}.dealCapitalization.uses`
                                      + `[${formKeyDealCapitalizationGroup}].name`}
                                  />
                                </Fragment>
                              )
                            }
                          </TableCell>
                          <TableCell isTextRight>
                            {
                              isEditMode && !dealCapitalizationGroup.isSystem && (
                                <ActionButton
                                  isLight
                                  isInline
                                  withNoHighlight
                                  withNoPadding
                                  onClick={(): void => {
                                    onDealCapitalizationUseGroupDelete(dealCapitalizationGroup.id);
                                  }}
                                >
                                  <RemoveIcon />
                                </ActionButton>
                              )
                            }
                          </TableCell>
                        </TableRow>
                      );
                    })
                  }
                  {
                    isEditMode && (
                      <TableRowAddNewItem
                        isLight
                        label="Add New Group"
                        colSpan={COLUMNS_TOTAL_EDIT}
                        onClick={onDealCapitalizationUseGroupAdd}
                      />
                    )
                  }
                </TableHead>
              </Table>
            ) : (
              <NoContentBlock isExtended>
                No deal capitalization uses groups
              </NoContentBlock>
            )
          }
        </ExpansionGroup>
        <ExpansionGroup
          isFixed
          isExpanded
          isDefaultExpanded
          withOffset
          headerComponent="Capitalization File"
        >
          <VisualEditorSectionBlock isDisabled withNoEdit isEditMode={isEditMode} />
        </ExpansionGroup>
      </div>
      <WidgetRow>
        <Widget defaultWidgetTitle="Equity Organization Chart">
          <VisualEditorSectionBlock isDisabled withNoEdit isEditMode={isEditMode} />
        </Widget>
        <Widget isFullHeight defaultWidgetTitle="Deal Metrics">
          <Fragment>
            <Table>
              <TableHead isSubHeader>
                <TableRow>
                  <TableCell colSpan={isEditMode ? COLUMNS_TOTAL_EDIT : COLUMNS_TOTAL_DEFAULT}>
                    Capital Stack
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody isPlain withZebraRows withBorderedCells>
                <TableRow>
                  <TableCell withNoPadding>
                    <VisualEditorSectionBlock isDisabled withNoEdit isEditMode={isEditMode} />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Table>
              <TableHead isSubHeader>
                <TableRow>
                  <TableCell>
                    Statistics
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody isPlain withZebraRows withBorderedCells>
                <TableRow>
                  <TableCell withNoPadding>
                    <VisualEditorSectionBlock isDisabled withNoEdit isEditMode={isEditMode} />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Fragment>
        </Widget>
      </WidgetRow>
    </SinglePageTab>
  );
});

export default DealTemplateCapitalization;
